import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { useFilters, usePagination, useSortBy, useTable } from "react-table";
import { COLUMNS } from "components/ActiveIssues/Columns";
import { ActiveIssuesCaseData } from "./ActiveIssuesInterfaces";

import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import TablePagination from "@mui/material/TablePagination";
import PerfectScrollbar from "react-perfect-scrollbar";
import { styled } from "@mui/material/styles";
import ProgressIndicator from "components/ProgressIndicator";
import { EntitlementContext } from "contexts/UserProfileContext";
import { FeaturesContext } from "contexts/FeaturesContext";
import { useLoadCounter } from "hooks/useLoadCounter";

export interface ActiveIssueTableProps {
  loading: boolean;
  lostEnergyCases: ActiveIssuesCaseData[];
  onChangeSelectedCaseId: (caseId: number) => void;
  selectedCaseId: number | undefined;
}
export const ActiveIssuesTable = ({
  loading,
  lostEnergyCases,
  onChangeSelectedCaseId,
  selectedCaseId
}: ActiveIssueTableProps): JSX.Element => {
  const tableLoadCount = useLoadCounter(loading);
  const [internalPageIndex, setInternalPageIndex] = useState(0);
  const [_internalSelectedId, setInternalSelectedId] = useState<
    undefined | number
  >(undefined);
  const { hasEntitlement } = useContext(EntitlementContext);
  const { hasFeature } = useContext(FeaturesContext);

  const columns = useMemo(() => COLUMNS, []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    gotoPage,
    setPageSize,
    state: { pageSize },
    prepareRow,
    setHiddenColumns
  } = useTable(
    {
      columns,
      data: lostEnergyCases,
      disableSortRemove: true,
      initialState: {
        pageSize: 50,
        hiddenColumns: ["predicted_energy_loss_next_month_mwh"]
      },
      // If shift held down whilst clicking column, allow multisort.
      isMultiSortEvent: event => event.shiftKey
    },
    useFilters,
    useSortBy,
    usePagination
  );

  useEffect(() => {
    const predictedLossesEnabled =
      hasFeature("lost_energy_predicted_losses") &&
      hasEntitlement("lostenergypredictedlosses-view");
    setHiddenColumns(
      predictedLossesEnabled ? [] : ["predicted_energy_loss_next_month_mwh"]
    );
  }, [hasEntitlement, hasFeature, setHiddenColumns]);

  const handleChangePage = useCallback(
    (newPage: number): void => {
      gotoPage(newPage);
      setInternalPageIndex(newPage);
    },
    [gotoPage]
  );

  useEffect(() => {
    /** If the selected ID from the chart is out of the table page, navigate user to the page. */
    setInternalSelectedId(prevState => {
      if (
        prevState !== selectedCaseId &&
        selectedCaseId !== undefined &&
        page.findIndex(el => el.values.case_id === selectedCaseId) === -1
      ) {
        const targetRow =
          rows.findIndex(el => el.values.case_id === selectedCaseId) + 1;
        let target =
          Math.floor(targetRow / pageSize) -
          (targetRow % pageSize === 0 ? 1 : 0);
        handleChangePage(target);
      }
      return selectedCaseId;
    });
  }, [selectedCaseId, page, rows, pageSize, lostEnergyCases, handleChangePage]);

  return (
    <FlexContainer>
      <TableContainer>
        <SiteTableScroll>
          <PerfectScrollbar>
            <TableWrapper
              data-qa="Active-Issues-Table"
              stickyHeader
              {...getTableProps()}
            >
              <TableHead>
                {headerGroups.map((headerGroup, headerGroupIndex) => (
                  <TableRow
                    {...headerGroup.getHeaderGroupProps()}
                    key={`htr_${headerGroupIndex}`}
                  >
                    {headerGroup.headers.map((column, headerIndex) => (
                      <ColumnHeaderCell
                        {...column.getSortByToggleProps()}
                        title=""
                        key={`htd_${headerIndex}`}
                        data-col={`${headerIndex}`}
                      >
                        {column.render("Header")}
                      </ColumnHeaderCell>
                    ))}
                  </TableRow>
                ))}
              </TableHead>
              <TableBody
                data-qa_loading={loading}
                data-qa_load_count={tableLoadCount}
                {...getTableBodyProps()}
              >
                {page.map((row, bodyIndex) => {
                  prepareRow(row);
                  return (
                    <TableBodyRow
                      data-qa_loading={loading}
                      {...row.getRowProps()}
                      selected={row.values.case_id === selectedCaseId}
                      onClick={() => {
                        onChangeSelectedCaseId(row.values.case_id);
                      }}
                      key={`btr_${bodyIndex}`}
                    >
                      {row.cells.map((cell, cellIndex) => {
                        return (
                          <TableBodyCell
                            {...cell.getCellProps()}
                            key={`btd_${cellIndex}`}
                            data-col={`${cellIndex}`}
                          >
                            {cell.render("Cell")}
                          </TableBodyCell>
                        );
                      })}
                    </TableBodyRow>
                  );
                })}
                {!loading && page.length === 0 && (
                  <TableRow>
                    <CenterAlignedCell colSpan={columns.length}>
                      <p>No data to display</p>
                    </CenterAlignedCell>
                  </TableRow>
                )}
              </TableBody>
            </TableWrapper>
            <ProgressIndicator loading={loading} />
          </PerfectScrollbar>
        </SiteTableScroll>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        rowsPerPage={pageSize}
        page={internalPageIndex}
        onPageChange={(_event, newPage) => handleChangePage(newPage)}
        onRowsPerPageChange={e => setPageSize(Number(e.target.value))}
      />
    </FlexContainer>
  );
};

const FlexContainer = styled("div")`
  flex: 1 1 900px;
  display: flex;
  flex-direction: column;
  overflow: auto;
  border: 1px solid rgba(224, 224, 224, 1);
  border-radius: 4px;
`;

const TableWrapper = styled(Table)`
  width: 100%;
`;

const ColumnHeaderCell = styled(TableCell)`
  padding: 10px 0 10px 10px;
  font-weight: bold;
  text-align: left;
  vertical-align: top;
`;

const TableBodyRow = styled(TableRow)`
  height: 50px;

  &:hover {
    background-color: rgba(76, 175, 80, 0.5);
    cursor: pointer;
  }
`;

const TableBodyCell = styled(TableCell)`
  width: 1%;
  padding: 5px 0 5px 10px;
`;

const SiteTableScroll = styled(Paper)`
  display: block;
  height: 60vh;
  min-height: 500px;
`;

const CenterAlignedCell = styled(TableCell)`
  text-align: center;
`;
